import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GIGS, useApp, formatPrice, getTierPrice, getTierOriginalPrice } from '@/context/AppContext';
import { SEOHead } from '@/components/SEOHead';
import {
  Clock, ArrowRight, Smartphone, Globe, Code2, Star, Cloud, Users, Megaphone,
  Check, X, ShieldCheck, FileText, Rocket, Repeat, Sparkles,
} from 'lucide-react';

/* ── Legacy one-time gig catalog (preserved) ── */
const iconMap = {
  'native-app-mvp': Smartphone,
  'enterprise-web-platform': Globe,
  'full-stack-application': Code2,
  'managed-hosting': Cloud,
  'virtual-assistant': Users,
  'ai-marketing-automation': Megaphone,
};
const accentMap = {
  'native-app-mvp': 'blue', 'enterprise-web-platform': 'orange', 'full-stack-application': 'blue',
  'managed-hosting': 'orange', 'virtual-assistant': 'blue', 'ai-marketing-automation': 'orange',
};

function getDeliveryLabel(gig) {
  if (gig.isRecurring) return 'Monthly retainer';
  if (gig.deliveryDays) return `${gig.deliveryDays}-day delivery`;
  const firstTier = gig.tiers?.[0];
  if (firstTier?.recurring) return 'Monthly retainer';
  if (firstTier?.delivery === 'One-time') return 'One-time + Monthly';
  return 'Custom timeline';
}

const GigCard = ({ gig, index, currency }) => {
  const Icon = iconMap[gig.id] || Code2;
  const isBlue = accentMap[gig.id] === 'blue';
  const tier0 = gig.tiers[0];
  const startingPrice = getTierPrice(tier0, currency);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: index * 0.06 }}
      whileHover={{ y: -4 }} data-testid={`gig-card-${gig.id}`}
    >
      <Link to={`/services/${gig.id}`} className="group block bg-white rounded-2xl border border-[color:var(--hairline)] hover:border-brand-blue/30 transition-[box-shadow,border-color] duration-200 hover:shadow-[var(--shadow-lift)] overflow-hidden h-full">
        <div className={`relative h-36 flex items-center justify-center ${isBlue ? 'bg-gradient-to-br from-brand-blue/5 to-brand-blue/10' : 'bg-gradient-to-br from-brand-orange/5 to-brand-orange/10'}`}>
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${isBlue ? 'bg-brand-blue/10' : 'bg-brand-orange/10'}`}>
            <Icon className={`w-8 h-8 ${isBlue ? 'text-brand-blue' : 'text-brand-orange'}`} strokeWidth={1.5} />
          </div>
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/90 backdrop-blur-sm text-[11px] font-semibold text-foreground border border-[color:var(--hairline)]">{gig.category}</span>
        </div>
        <div className="p-5">
          <h3 className="text-base font-bold text-foreground mb-1.5 group-hover:text-brand-blue transition-colors duration-200">{gig.title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-2">{gig.shortDesc}</p>
          <div className="flex items-center justify-between pt-4 border-t border-[color:var(--hairline)]">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Clock className="w-3.5 h-3.5" />{getDeliveryLabel(gig)}</div>
            <div className="text-right">
              <span className="text-[11px] text-muted-foreground">{gig.isRecurring ? 'From' : 'Starting at'}</span>
              <p className="text-lg font-bold text-brand-orange">{formatPrice(startingPrice, currency)}{gig.isRecurring && <span className="text-xs font-medium text-muted-foreground">/mo</span>}</p>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

/* ── Feature comparison matrix (faithful to the plan spec; not fabricated) ── */
const COMPARISON = [
  { label: 'Scoped web application', basic: true, premium: 'Advanced', elite: 'Custom', custom: 'Custom' },
  { label: 'Secure login & authentication', basic: true, premium: true, elite: true, custom: true },
  { label: 'User dashboard', basic: 'Basic', premium: 'Expanded', elite: 'Advanced', custom: 'Custom' },
  { label: 'Admin functionality', basic: 'Basic', premium: true, elite: true, custom: true },
  { label: 'Multiple roles & workflows', basic: false, premium: true, elite: true, custom: true },
  { label: 'Dashboards & reporting', basic: false, premium: 'Expanded', elite: 'Advanced', custom: 'Custom' },
  { label: 'Mobile companion app', basic: false, premium: 'In scope', elite: 'By quote', custom: true },
  { label: 'Integrations / automation', basic: 'Add-on', premium: 'By quote', elite: 'Up to 2', custom: 'Extensive' },
  { label: 'Managed hosting & deployment', basic: true, premium: true, elite: true, custom: true },
  { label: 'Maintenance & security updates', basic: true, premium: true, elite: true, custom: true },
  { label: 'Engineering hours / month', basic: '1 hr changes', premium: '8 hrs', elite: '12 hrs', custom: 'Scoped' },
];

const PROCESS = [
  { icon: FileText, title: 'Submit requirements', desc: 'Tell us what you want to build — in plain language, no tech spec needed.' },
  { icon: ShieldCheck, title: 'Scope approval', desc: 'We scope the work and you approve it before anything is built. No moving goalposts.' },
  { icon: Repeat, title: 'Agreement & subscribe', desc: 'Accept the agreement and start your managed subscription. First payment before kickoff.' },
  { icon: Rocket, title: 'Launch & manage', desc: 'An eligible first version can launch in as little as 15 business days, then we manage it.' },
];

const money = (n) => `$${Number(n).toLocaleString()}`;

const Cell = ({ v }) => {
  if (v === true) return <Check className="w-4 h-4 text-brand-blue mx-auto" data-testid="cmp-yes" />;
  if (v === false) return <X className="w-4 h-4 text-slate-300 mx-auto" data-testid="cmp-no" />;
  return <span className="text-xs font-semibold text-foreground">{v}</span>;
};

const PlanCard = ({ plan, cycle, onSelect, index }) => {
  const highlight = plan.highlight;
  const quote = plan.quote_only;
  const price = quote ? null : (cycle === 'annual' ? plan.annual_usd : plan.monthly_usd);
  const features = plan.features || [];
  const limits = plan.limitations || [];
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: index * 0.07 }}
      className={`relative flex flex-col rounded-2xl border bg-white p-6 ${highlight ? 'border-brand-blue shadow-[var(--shadow-lift)] ring-1 ring-brand-blue/20' : 'border-[color:var(--hairline)]'}`}
      data-testid={`plan-card-${plan.id}`}
    >
      {highlight && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-brand-blue text-white text-[11px] font-bold shadow-sm">
          <Sparkles className="w-3 h-3" /> Most popular
        </span>
      )}
      <h3 className="text-lg font-bold text-foreground">{plan.name}</h3>
      <p className="mt-1 text-sm text-muted-foreground leading-relaxed min-h-[40px]">{plan.tagline}</p>

      <div className="mt-4 mb-1">
        {quote ? (
          <p className="text-3xl font-bold text-foreground" data-testid={`plan-price-${plan.id}`}>Custom</p>
        ) : (
          <div className="flex items-end gap-1.5">
            <p className="text-3xl font-bold text-foreground" data-testid={`plan-price-${plan.id}`}>{money(price)}</p>
            <span className="mb-1 text-sm font-medium text-muted-foreground">{cycle === 'annual' ? '/year' : '/month'}</span>
          </div>
        )}
        {!quote && cycle === 'annual' && (
          <p className="text-[12px] text-muted-foreground mt-0.5">{money(plan.monthly_usd)}/mo · billed annually</p>
        )}
        {quote && <p className="text-[12px] text-muted-foreground mt-0.5">Scoped to your requirements</p>}
      </div>

      <button
        onClick={() => onSelect(plan.id)}
        className={`mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-[background-color,box-shadow,border-color] duration-200 focus-visible:outline-none focus-visible:ring-4 ${highlight ? 'bg-brand-blue text-white hover:bg-brand-blue-dark focus-visible:ring-brand-blue/30' : 'bg-foreground text-white hover:opacity-90 focus-visible:ring-foreground/20'}`}
        data-testid={`plan-cta-${plan.id}`}
      >
        {plan.cta || (quote ? 'Request a Quote' : `Choose ${plan.name}`)} <ArrowRight className="w-4 h-4" />
      </button>

      <ul className="mt-5 space-y-2.5">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-foreground">
            <Check className="w-4 h-4 text-brand-blue shrink-0 mt-0.5" /> <span className="leading-snug">{f}</span>
          </li>
        ))}
      </ul>
      {limits.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[color:var(--hairline)]">
          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">Good to know</p>
          <ul className="space-y-2">
            {limits.map((l, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[13px] text-muted-foreground">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-slate-300 shrink-0" /> <span className="leading-snug">{l}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </motion.div>
  );
};

export default function GigCatalog() {
  const { currency, plansConfig } = useApp();
  const navigate = useNavigate();
  const [cycle, setCycle] = useState('monthly');
  const plans = plansConfig?.plans || [];
  const commitment = plansConfig?.commercial_rules?.standard_commitment_months || 12;

  const selectPlan = (planId) => {
    const chosen = plans.find((p) => p.id === planId);
    // Persist the selected plan + billing frequency so it survives sign-in and
    // page refresh, then preselects in the onboarding wizard.
    try {
      localStorage.setItem('ml_daas_intent', JSON.stringify({ plan_id: planId, cycle, savedAt: Date.now() }));
    } catch (e) { /* noop */ }
    // Custom/quote plans go through the consultation/quote funnel; standard plans
    // head into the authenticated onboarding → project creation journey.
    if (chosen?.quote_only) { navigate(`/landing-page?plan=${planId}`); return; }
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="gig-catalog-page">
      <SEOHead
        title="Plans & Pricing | MediaLabs Managed Development"
        description="Fully managed development subscriptions. Basic $290, Premium $990, Elite $1,490 per month, or request a custom quote. Launch, manage and grow your software without an in-house team."
        path="/services"
      />

      <div className="pt-32 sm:pt-36 lg:pt-40 pb-10 sm:pb-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} className="text-center mb-10">
            <span className="text-xs font-semibold text-brand-blue uppercase tracking-wider">Managed Development Subscriptions</span>
            <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">Simple plans. Fully managed software.</h1>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              One predictable monthly subscription to launch, manage and grow your business software — without the burden of hiring an in-house engineering team.
            </p>
          </motion.div>

          {/* Billing toggle */}
          <div className="flex items-center justify-center gap-3 mb-10" data-testid="billing-toggle">
            <span className={`text-sm font-semibold ${cycle === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}>Monthly</span>
            <button
              role="switch" aria-checked={cycle === 'annual'}
              onClick={() => setCycle((c) => (c === 'monthly' ? 'annual' : 'monthly'))}
              className={`relative w-14 h-7 rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-blue/30 ${cycle === 'annual' ? 'bg-brand-blue' : 'bg-slate-300'}`}
              data-testid="billing-toggle-btn"
            >
              <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform duration-200 ${cycle === 'annual' ? 'translate-x-7' : ''}`} />
            </button>
            <span className={`text-sm font-semibold ${cycle === 'annual' ? 'text-foreground' : 'text-muted-foreground'}`}>Annual</span>
            <span className="ml-1 text-[11px] text-muted-foreground">(12 months upfront)</span>
          </div>

          {/* Plan cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 items-stretch">
            {plans.map((plan, i) => (
              <PlanCard key={plan.id} plan={plan} cycle={cycle} index={i} onSelect={selectPlan} />
            ))}
          </div>

          {/* Commitment / first payment note */}
          <p className="text-center text-xs text-muted-foreground mt-6 max-w-2xl mx-auto">
            Standard {commitment}-month commitment · monthly or annual upfront · no separate setup fee · first payment before development kickoff, after scope and contract approval.
          </p>

          {/* Comparison table */}
          <div className="mt-16" data-testid="plan-comparison">
            <h2 className="text-2xl font-bold text-foreground text-center mb-6">Compare the plans</h2>
            <div className="overflow-x-auto rounded-2xl border border-[color:var(--hairline)] bg-white">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="border-b border-[color:var(--hairline)] bg-slate-50/60">
                    <th className="text-left px-5 py-4 font-semibold text-foreground">Capability</th>
                    <th className="px-4 py-4 font-semibold text-foreground text-center">Basic</th>
                    <th className="px-4 py-4 font-semibold text-brand-blue text-center">Premium</th>
                    <th className="px-4 py-4 font-semibold text-foreground text-center">Elite</th>
                    <th className="px-4 py-4 font-semibold text-foreground text-center">Custom</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON.map((row, i) => (
                    <tr key={i} className="border-b border-[color:var(--hairline)] last:border-0 hover:bg-slate-50/40">
                      <td className="px-5 py-3 text-foreground">{row.label}</td>
                      <td className="px-4 py-3 text-center"><Cell v={row.basic} /></td>
                      <td className="px-4 py-3 text-center bg-brand-blue/[0.03]"><Cell v={row.premium} /></td>
                      <td className="px-4 py-3 text-center"><Cell v={row.elite} /></td>
                      <td className="px-4 py-3 text-center"><Cell v={row.custom} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Process + Ownership */}
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold text-foreground mb-6">How development works</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {PROCESS.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div key={i} className="rounded-2xl border border-[color:var(--hairline)] bg-white p-5" data-testid={`process-step-${i}`}>
                      <div className="w-10 h-10 rounded-xl bg-brand-blue/10 flex items-center justify-center mb-3">
                        <Icon className="w-5 h-5 text-brand-blue" />
                      </div>
                      <p className="text-[11px] font-bold text-brand-blue">STEP {i + 1}</p>
                      <h3 className="text-base font-bold text-foreground mt-0.5">{s.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed mt-1">{s.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="rounded-2xl border border-[color:var(--hairline)] bg-gradient-to-br from-brand-blue/5 to-white p-6" data-testid="ownership-card">
              <div className="w-10 h-10 rounded-xl bg-brand-orange/10 flex items-center justify-center mb-3">
                <ShieldCheck className="w-5 h-5 text-brand-orange" />
              </div>
              <h3 className="text-lg font-bold text-foreground">Clear ownership</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mt-2">
                You own your business data — always. Custom software IP rights follow your approved contract, with exceptions only for existing MediaLabs IP and third-party licences.
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed mt-3">
                Fully managed means we handle hosting, maintenance and support — it does not mean unlimited development. Additional work is scoped, quoted and approved first, and any third-party costs are disclosed.
              </p>
            </div>
          </div>

          {/* Legacy one-time packages (preserved) */}
          <div className="mt-20" data-testid="legacy-gigs">
            <div className="text-center mb-8">
              <span className="text-xs font-semibold text-brand-orange uppercase tracking-wider">One-time project packages</span>
              <h2 className="mt-2 text-2xl font-bold text-foreground">Prefer a fixed-scope, one-time build?</h2>
              <p className="mt-2 text-sm text-muted-foreground max-w-xl mx-auto">Our original productized packages are still available for one-off projects.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {GIGS.map((gig, i) => <GigCard key={gig.id} gig={gig} index={i} currency={currency} />)}
            </div>
          </div>

          {/* Footer CTA */}
          <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mt-16">
            <p className="text-sm text-muted-foreground mb-4">Not sure which plan fits? Let's talk it through.</p>
            <Link to="/landing-page" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-brand-orange text-white text-sm font-bold hover:bg-brand-orange-dark transition-colors duration-200" data-testid="services-consult-cta">
              Book a free consultation <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
